/**
 * `<ProDocExtractionReview>` — ProDoc's extraction review (engine W15,
 * FR-E20 – FR-E23, FR-E41), and `<ProDocExtractionQueue>`, what waits for it.
 *
 * An `auto` extraction that does not reconcile, whose identity is weak, that
 * AI helped, or that the organisation does not accept automatically, waits
 * for a person with `extraction:can_update` (FR-E19). They see the page and
 * what was read from it — **the failing checks first** — and:
 *
 * - **correct** a field where the page says otherwise. A correction is its own
 *   row, never an edit of the extraction (FR-E21): the screen sends the
 *   revision, the kernel re-checks the corrected view (D11), and the
 *   rechecked checks stand beside the originals. Money is typed in rupees and
 *   sent as integer paise; nothing is ever a float (FR-E16);
 * - **accept** it — the app is told, and it reads the accepted view — or
 *   **reject** it, which ends it: reading the document again is a new request
 *   (FR-E20). Either takes a comment.
 *
 * An accepted extraction stays accepted (FR-E23): a later correction is a new
 * revision **with a reason**. An extraction its app reviews (`review: app`) is
 * the app's to decide, and a rejected one takes nothing more. Without
 * `extraction:can_update` this is `<ProDocExtraction>` — read, not decided.
 *
 * W24 (FR-56, D73): **the review walks its queue in the queue's order.** The
 * queue hands the host its order (`onOpen`'s second argument, ids only); the host
 * keeps it with the review's address (this app: in the route's state) and passes
 * it back as `walk`. Then *Previous* / *Next* and **J / K** move along it — never
 * from a text field, never while a dialog is open — and nowhere else.
 */
import { Alert, Badge, Button, Card, CardBody, CardHeader, DataTable, Dialog, EmptyState, Input, Skeleton, SkeletonRegion, Textarea, cn, formatNumber, useToast, type ColumnDef } from '@fiveox/ui'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import { ExtractionBody, ExtractionStateBadge, ExtractionView, ReconciledBadge, ReviewStateAlert, profileOf, useExtraction, type FieldEditor } from './extraction'
import type { ImageOpener, PdfOpener } from './evidence-page'
import {
  MAX_CORRECTION_REASON,
  MAX_EXTRACTION_COMMENT,
  checkLabel,
  failingGating,
  jobReasonText,
  reviewReasonLabel,
  valueAt,
  valueFromText,
  type Extraction,
  type ExtractionQueueItem,
  type ExtractionRecord,
  type ExtractionReviewState,
  type FieldKind,
  type FieldValue,
  type ParseJob,
} from './extraction-model'
import { holdsMask } from './personal-data'
import { JOB_POLL_MS, useFollowedJob } from './jobs'
import { when } from './bits'
import { errorText, useLoad, useProDocAccess, useProDocClient, useQueueKeys, type HostProps } from './hooks'
import { EXTRACTION, documentTypeLabel, isIllegalTransition, type DocumentTypeOption, type ProDocClient, type ProDocDocument } from './prodoc-client'

/* ---------------------------------------------------------- the walk */

/** One extraction of a queue's walk — ids only, so a host may keep a walk in its history without a title in it. */
export interface ExtractionStep {
  document_id: string
  id: string
}

/** A walk is the queue's one page; anything longer was not handed over by it. */
const WALK_LIMIT = 1000

/**
 * A walk as a host kept it (D73) — read defensively, since history state is the
 * browser's: the steps with both ids, in their order, or `null` when there is none.
 */
export function extractionWalkOf(raw: unknown): ExtractionStep[] | null {
  if (!Array.isArray(raw)) return null
  const steps: ExtractionStep[] = []
  for (const entry of raw.slice(0, WALK_LIMIT)) {
    const r = entry !== null && typeof entry === 'object' ? (entry as Record<string, unknown>) : null
    if (r && typeof r.document_id === 'string' && r.document_id && typeof r.id === 'string' && r.id) steps.push({ document_id: r.document_id, id: r.id })
  }
  return steps.length > 0 ? steps : null
}

/** Where an extraction stands in a walk: its place (`-1` when it is not in it) and its neighbours — never past either end. */
export function walkAt(walk: readonly ExtractionStep[] | null | undefined, extractionId: string): { at: number; prev: ExtractionStep | null; next: ExtractionStep | null } {
  const at = (walk ?? []).findIndex((s) => s.id === extractionId)
  if (!walk || at < 0) return { at: -1, prev: null, next: null }
  return { at, prev: walk[at - 1] ?? null, next: walk[at + 1] ?? null }
}

export interface ProDocExtractionReviewProps extends HostProps {
  documentId: string
  extractionId: string
  /**
   * W24 (D73): the queue's order this review walks — handed by `<ProDocExtractionQueue>`'s
   * `onOpen` and kept by the host. With `onWalk`, *Previous* / *Next* and J / K move along it.
   */
  walk?: readonly ExtractionStep[] | null
  /** Open another extraction of the walk — the host's navigation, keeping the walk. */
  onWalk?: (step: ExtractionStep) => void
  /** The host's way back — its `<Link>` to the document or the queue. */
  back?: ReactNode
  /** The host's way to the document's own screen. Not offered without it. */
  onOpenDocument?: (document: ProDocDocument) => void
  /** After an accept or a reject, with the record as it now stands. */
  onDecided?: (record: ExtractionRecord) => void
  /** How a PDF is opened — PDF.js unless a host or a test says otherwise. */
  openPdf?: PdfOpener
  /** How a scanned image is decoded — the browser's own decoder unless a host or a test says otherwise. */
  openImage?: ImageOpener
  /** How soon a recheck is first asked about again. A test passes a few milliseconds. */
  jobPollMs?: number
  className?: string
}

export function ProDocExtractionReview(props: ProDocExtractionReviewProps) {
  const { documentId, extractionId, walk, onWalk, back, onOpenDocument, onDecided, openPdf, openImage, jobPollMs = JOB_POLL_MS, className } = props
  const client = useProDocClient(props)
  const access = useProDocAccess(client)
  // Bumped after every write, so the extraction — its corrections, its rechecks, its state — is read again.
  const [revision, setRevision] = useState(0)
  const loaded = useExtraction(client, documentId, extractionId, revision)
  const reviewer = access.can(EXTRACTION, 'can_update')

  // D73: the queue's order, walked with J / K beside the buttons — only along it, and only with somewhere to go.
  const place = walkAt(onWalk ? walk : null, extractionId)
  const goPrev = place.prev && onWalk ? () => onWalk(place.prev!) : null
  const goNext = place.next && onWalk ? () => onWalk(place.next!) : null
  useQueueKeys({ next: goNext, prev: goPrev })

  if (access.loading) return <Skeleton className={cn('h-[60vh] rounded-2xl', className)} />

  return (
    <div className={cn('space-y-6', className)}>
      {place.at >= 0 && walk ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">{back}</div>
          <span className="flex items-center gap-1 text-sm" role="group" aria-label="The extraction queue">
            <Button variant="ghost" size="sm" disabled={!goPrev} onClick={() => goPrev?.()} aria-label="Previous extraction" title="Previous (K)">
              <ChevronLeft size={16} aria-hidden />
            </Button>
            <span className="text-foreground shrink-0 font-medium">
              {place.at + 1} of {walk.length} <span className="text-muted-foreground font-normal">in the queue</span>
            </span>
            <Button variant="ghost" size="sm" disabled={!goNext} onClick={() => goNext?.()} aria-label="Next extraction" title="Next (J)">
              <ChevronRight size={16} aria-hidden />
            </Button>
            <span className="text-muted-foreground hidden text-xs sm:inline">J next · K previous</span>
          </span>
        </div>
      ) : (
        back
      )}
      <ExtractionBody
        loaded={loaded}
        client={client}
        openPdf={openPdf}
        openImage={openImage}
        onOpenDocument={onOpenDocument}
        render={
          reviewer
            ? (doc, extraction) => (
                <ReviewPanel
                  key={extraction.id}
                  client={client}
                  doc={doc}
                  extraction={extraction}
                  types={loaded.types}
                  openPdf={openPdf}
                  openImage={openImage}
                  jobPollMs={jobPollMs}
                  onChanged={() => setRevision((n) => n + 1)}
                  onDecided={onDecided}
                />
              )
            : undefined
        }
      />
    </div>
  )
}

/* -------------------------------------------------------- the panel */

interface Draft {
  text: string
  kind: FieldKind
}

function ReviewPanel({
  client,
  doc,
  extraction,
  types,
  openPdf,
  openImage,
  jobPollMs,
  onChanged,
  onDecided,
}: {
  client: ProDocClient
  doc: ProDocDocument
  extraction: Extraction
  types: DocumentTypeOption[]
  openPdf?: PdfOpener
  openImage?: ImageOpener
  jobPollMs: number
  onChanged: () => void
  onDecided?: (record: ExtractionRecord) => void
}) {
  const { toast } = useToast()
  const review = extraction.review
  const state = review?.state ?? 'pending'
  // FR-E20 / FR-E23: a waiting extraction is corrected and decided; an accepted one corrected with a reason; nothing else here.
  const correctable = state === 'needs_review' || state === 'accepted'
  const decidable = state === 'needs_review'

  const [draft, setDraft] = useState<Record<string, Draft>>({})
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [recheck, setRecheck] = useState<ParseJob | null>(null)
  const [comment, setComment] = useState('')
  const [deciding, setDeciding] = useState<'accept' | 'reject' | null>(null)
  const [decideError, setDecideError] = useState('')
  const [rejecting, setRejecting] = useState(false)

  // The kernel's recheck of the revision just saved (D11): followed until it ends, then the extraction is read again.
  const followed = useFollowedJob(client, recheck, jobPollMs, (job) => {
    if (job.status === 'completed') onChanged()
  })
  const rechecking = followed.following

  const fields = extraction.view ?? extraction.fields
  const parsed = useMemo(() => {
    const values: { field_path: string; value: FieldValue }[] = []
    const errors: Record<string, string> = {}
    for (const [path, d] of Object.entries(draft)) {
      const answer = valueFromText(d.text, d.kind)
      if ('error' in answer) errors[path] = answer.error
      // A field typed back to what it already holds is not a correction — a masked field left masked included.
      else if (answer.value === (valueAt(fields, path) ?? null)) continue
      // W23 (FR-51): a masked form is not a value, and is never sent back as one.
      else if (typeof answer.value === 'string' && holdsMask(answer.value)) errors[path] = 'Type the whole value as printed — a masked one cannot be saved.'
      else values.push({ field_path: path, value: answer.value })
    }
    return { values, errors }
  }, [draft, fields])

  const editor: FieldEditor | null = correctable
    ? {
        draft: Object.fromEntries(Object.entries(draft).map(([p, d]) => [p, d.text])),
        errors: parsed.errors,
        edit: (path, text, kind) => setDraft((all) => ({ ...all, [path]: { text, kind } })),
        undo: (path) =>
          setDraft((all) => {
            const next = { ...all }
            delete next[path]
            return next
          }),
      }
    : null

  const pending = Object.keys(draft).length
  const needsReason = state === 'accepted'
  const reasonTooLong = reason.trim().length > MAX_CORRECTION_REASON
  const invalid = Object.keys(parsed.errors).length > 0

  const save = async () => {
    if (saving || invalid || reasonTooLong) return
    if (parsed.values.length === 0) {
      setDraft({})
      return
    }
    if (needsReason && !reason.trim()) {
      setSaveError('An accepted extraction is corrected with a reason — say why it changes.')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      const answer = await client.parsing.correct(doc.id, extraction.id, { corrections: parsed.values, reason: reason.trim() || null })
      setDraft({})
      setReason('')
      setRecheck(answer.recheck_job)
      toast(`${parsed.values.length === 1 ? 'The correction is' : `${parsed.values.length} corrections are`} saved as revision ${answer.revision}. The checks are run again on it.`)
      onChanged()
    } catch (e) {
      setSaveError(errorText(e, 'The corrections were not saved.'))
      if (isIllegalTransition(e)) onChanged()
    } finally {
      setSaving(false)
    }
  }

  const decide = async (action: 'accept' | 'reject', text: string) => {
    if (deciding) return
    setDeciding(action)
    setDecideError('')
    try {
      const record =
        action === 'accept'
          ? await client.parsing.accept(doc.id, extraction.id, { comment: text })
          : await client.parsing.reject(doc.id, extraction.id, { comment: text })
      setRejecting(false)
      toast(action === 'accept' ? 'Accepted. Its app reads what was read, as corrected.' : 'Rejected. Reading it again is a new request.')
      onDecided?.(record)
      onChanged()
    } catch (e) {
      setDecideError(errorText(e, 'The decision was not recorded.'))
      // Decided by someone else first: read it again rather than retry.
      if (isIllegalTransition(e)) onChanged()
    } finally {
      setDeciding(null)
    }
  }

  // The checks the decision is made on: the recheck of the latest revision once it exists, else the originals.
  const current = review && review.revision > 0 && review.rechecked_revision === review.revision && extraction.view_checks ? extraction.view_checks : extraction.checks
  const failing = failingGating(current)
  const commentTooLong = comment.length > MAX_EXTRACTION_COMMENT

  const checksNote =
    review && review.revision > 0 ? (
      <RecheckNote revision={review.revision} rechecked={review.rechecked_revision} job={followed.job} rechecking={rechecking} error={followed.error} onRefresh={onChanged} />
    ) : null

  const aside = (
    <>
      <ReviewStateAlert record={extraction} />
      {pending > 0 && (
        <Card>
          <CardHeader title={`${pending === 1 ? 'A correction' : `${pending} corrections`} not saved`} description="Saved together as the next revision; the extraction itself is never changed." />
          <CardBody className="space-y-3">
            <Input
              label={needsReason ? 'Why it changes' : 'Why (optional)'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              hint={needsReason ? 'It is accepted already, so a correction is a new revision with a reason.' : 'Recorded with the revision.'}
              error={reasonTooLong ? `Up to ${formatNumber(MAX_CORRECTION_REASON)} characters.` : null}
            />
            {saveError && <Alert tone="error">{saveError}</Alert>}
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setDraft({})} disabled={saving}>
                Discard
              </Button>
              <Button onClick={() => void save()} loading={saving} loadingText="Saving…" disabled={invalid || reasonTooLong}>
                Save {pending === 1 ? 'the correction' : `${pending} corrections`}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
      {decidable && (
        <Card>
          <CardHeader
            title="Your decision"
            description={failing.length ? `${failing.length === 1 ? 'A gating check does' : `${failing.length} gating checks do`} not hold. Correct what the page says otherwise, or decide as it stands.` : 'Every gating check holds.'}
          />
          <CardBody className="space-y-3">
            {failing.length > 0 && (
              <ul className="text-muted-foreground list-disc pl-5 text-sm" aria-label="Failing checks">
                {failing.map((c) => (
                  <li key={c.name}>{checkLabel(c.name)}</li>
                ))}
              </ul>
            )}
            <Textarea
              label="Comment"
              labelAside={<span className="text-muted-foreground text-xs">{`${formatNumber(comment.length)} / ${formatNumber(MAX_EXTRACTION_COMMENT)}`}</span>}
              hint="Optional. Recorded with the decision; its app is told."
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              error={commentTooLong ? `Up to ${formatNumber(MAX_EXTRACTION_COMMENT)} characters.` : null}
            />
            {decideError && !rejecting && <Alert tone="error">{decideError}</Alert>}
            {(pending > 0 || rechecking) && (
              <p className="text-muted-foreground text-xs">{pending > 0 ? 'Save or discard your corrections first.' : 'Wait for the checks to run on your corrections.'}</p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDecideError('')
                  setRejecting(true)
                }}
                disabled={deciding !== null || pending > 0}
              >
                Reject…
              </Button>
              <Button
                onClick={() => void decide('accept', comment.trim())}
                loading={deciding === 'accept'}
                loadingText="Accepting…"
                disabled={deciding !== null || pending > 0 || rechecking || commentTooLong}
              >
                {failing.length ? 'Accept as it stands' : 'Accept'}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  )

  return (
    <>
      <ExtractionView
        client={client}
        doc={doc}
        extraction={extraction}
        types={types}
        openPdf={openPdf}
        openImage={openImage}
        editor={editor}
        aside={aside}
        checksNote={checksNote}
        checksFirst
      />
      <Dialog open={rejecting} onClose={() => setRejecting(false)} title={`Reject what was read from “${doc.title}”?`} error={decideError || null}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (!commentTooLong) void decide('reject', comment.trim())
          }}
        >
          <Textarea
            label="Why"
            hint="Optional — recorded with the decision; its app is told."
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            error={commentTooLong ? `Up to ${formatNumber(MAX_EXTRACTION_COMMENT)} characters.` : null}
          />
          <p className="text-muted-foreground text-sm">
            It ends here: nothing read from it reaches its app, and nothing is deleted. Reading {documentTypeLabel(profileOf(extraction), types).toLowerCase()} again is a new request from
            the document.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setRejecting(false)} disabled={deciding !== null}>
              Keep reviewing
            </Button>
            <Button type="submit" variant="destructive" loading={deciding === 'reject'} loadingText="Rejecting…" disabled={commentTooLong}>
              Reject
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  )
}

/** Where the checks of the latest revision stand: being run, run, or could not be run on it. */
function RecheckNote({
  revision,
  rechecked,
  job,
  rechecking,
  error,
  onRefresh,
}: {
  revision: number
  rechecked: number | null
  job: ParseJob | null
  rechecking: boolean
  error: string
  onRefresh: () => void
}) {
  if (rechecking) return <Alert tone="info">Running the checks on revision {revision}…</Alert>
  if (job && (job.status === 'refused' || job.status === 'failed')) {
    return (
      <Alert tone="error">
        The checks could not be run on revision {revision}: {jobReasonText(job.reason) || 'the corrected fields do not fit what this kind of document holds.'} Correct it again.
      </Alert>
    )
  }
  if (error) return <Alert tone="error">{error}</Alert>
  if (rechecked === revision) return <p className="text-muted-foreground text-xs">Checked again after revision {revision} — beside each check, how it stands now.</p>
  return (
    <Alert tone="info">
      Revision {revision} is being checked.{' '}
      <Button variant="link" onClick={onRefresh}>
        Look again
      </Button>
    </Alert>
  )
}

/* ---------------------------------------------------------- the queue */

export interface ProDocExtractionQueueProps extends HostProps {
  /** Which state to list — what waits for a person, unless said. */
  state?: ExtractionReviewState
  /**
   * An extraction picked from the queue — the host's way to its review — with the
   * queue's order as shown (W24, D73: ids only), for the review to walk.
   */
  onOpen: (item: ExtractionQueueItem, walk: ExtractionStep[]) => void
  /** Offered when nothing waits — the host's way to its documents. */
  emptyAction?: ReactNode
  className?: string
}

/** The queue is read in one page, oldest first; the rest follow as these are decided. */
export const EXTRACTION_QUEUE_LIMIT = 100

/** What waits for a reviewer (FR-E20): each extraction, the document it read, and why it waits. */
export function ProDocExtractionQueue(props: ProDocExtractionQueueProps) {
  const { state = 'needs_review', onOpen, emptyAction, className } = props
  const client = useProDocClient(props)
  const access = useProDocAccess(client)
  const reviewer = access.can(EXTRACTION, 'can_update')
  const queue = useLoad(() => (reviewer ? client.parsing.queue({ state, limit: EXTRACTION_QUEUE_LIMIT }) : Promise.resolve(null)), [client, state, reviewer])
  const options = useLoad(() => client.documents.options(), [client])
  const types = options.data?.document_types ?? []

  if (access.loading) return <Skeleton className={cn('h-72 rounded-2xl', className)} />
  if (!reviewer) {
    return (
      <EmptyState
        className={className}
        title="You can’t review extractions here"
        description="Correcting, accepting and rejecting what ProDoc read is a reviewer’s. An administrator can change your ProDoc role in the FiveOX console."
      />
    )
  }
  if (queue.loading && !queue.data) {
    return (
      <SkeletonRegion label="Loading the extraction queue" className={cn('space-y-3', className)}>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </SkeletonRegion>
    )
  }
  if (!queue.data) {
    return (
      <Alert tone="error" className={className}>
        <p className="font-medium">The extraction queue could not be loaded</p>
        <p className="mt-1">
          {queue.error}{' '}
          <Button variant="link" onClick={() => void queue.reload()}>
            Try again
          </Button>
        </p>
      </Alert>
    )
  }
  const items = queue.data.items
  if (items.length === 0) {
    return (
      <EmptyState
        className={className}
        title="Nothing waiting for review"
        description="What ProDoc reads waits here when a check does not add up, whose document it is is not certain, AI helped, or the organisation accepts nothing automatically."
        action={emptyAction}
      />
    )
  }

  const columns: ColumnDef<ExtractionQueueItem>[] = [
    {
      key: 'document',
      label: 'Document',
      width: 260,
      required: true,
      render: (item) => <span className="text-foreground font-medium">{item.document?.title || 'A document'}</span>,
    },
    { key: 'profile', label: 'Read as', width: 150, render: (item) => documentTypeLabel(profileOf(item), types) },
    {
      key: 'reasons',
      label: 'Why it waits',
      width: 280,
      render: (item) => (
        <span className="flex flex-wrap gap-1">
          {(item.review?.reasons ?? []).map((r) => (
            <Badge key={r} tone="warning" size="sm">
              {reviewReasonLabel(r)}
            </Badge>
          ))}
        </span>
      ),
    },
    { key: 'reconciled', label: 'Adds up', width: 130, render: (item) => <ReconciledBadge reconciled={item.reconciled} size="sm" /> },
    { key: 'state', label: 'State', width: 130, render: (item) => <ExtractionStateBadge state={item.review?.state} size="sm" /> },
    { key: 'read', label: 'Read', width: 120, render: (item) => (item.created_at ? when(item.created_at) : '—') },
  ]

  return (
    <div className={cn('space-y-3', className)}>
      <p className="text-muted-foreground text-sm">
        {queue.data.total > items.length ? `The oldest ${items.length} of ${formatNumber(queue.data.total)}` : `${items.length} waiting`} — pick one to review it beside its page.
      </p>
      <DataTable columns={columns} rows={items} rowKey={(item) => item.id} onRowClick={(item) => onOpen(item, items.map((i) => ({ document_id: i.document_id, id: i.id })))} />
    </div>
  )
}
